import loadable from '@loadable/component';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getLanguage } from '@travel/i18n';
import RanDataFormInputs from '@travel/traveler-core/components/RanDataFormInputs';
import { useDeviceTypes } from '@travel/traveler-core/hooks/useDeviceType';
import { getRanData } from '@travel/traveler-core/store/ranStore/selectors';
import { cx, parseQueryString, stringify } from '@travel/utils';

import useCustomFixedStyling from 'components/Header/useCustomFixedStyling';
import SalesPromotionInput from 'components/SalesPromotionInput';
import { SearchFormWithRef } from 'components/SearchForm';
import type { SearchFormRef, SearchPanelProps, SubmitParams } from 'components/SearchForm/types';

import { getHeightIfSmartBannerPresent } from 'store/banner/selectors';
import { updateAreaName } from 'store/providerList/actions';
import { getAreaName } from 'store/providerList/selectors';

import { autoFilledCheckInDate, autoFilledCheckOutDate } from 'constants/date';
import type { SortKeysType } from 'constants/provider';
import usePlaceRedirect from 'hooks/usePlaceRedirect';
import type { SearchFormQuery } from 'ProviderList-Types';
import { savedSearchStore } from 'utils/savedSearch';
import { getAllowedRedirectSearchQuery } from 'utils/search';

import type { FilterFormProps } from '../FilterForm';
import FilterForm from '../FilterForm';

import styles from './searchBar.module.scss';

const FilterContainer = loadable(
  () =>
    import(
      /* webpackMode: "lazy" */
      /* webpackFetchPriority: "low" */
      /* webpackChunkName: "component-FilterContainer" */
      '../FilterContainer'
    ),
  { ssr: false },
);

type Props = {
  searchFormRef: React.MutableRefObject<SearchFormRef>;
  className?: string;
  query: SearchFormQuery;
  pageIdentifier: string;
  shouldOpenDatePickerOnMount?: boolean;
  onRemoveCoupon?: () => void;
  setSortKey?: React.Dispatch<React.SetStateAction<SortKeysType>>;
  filterFormProps?: FilterFormProps;
  onSearchFilter: () => void;
};

function SearchBar(props: Props) {
  const {
    searchFormRef,
    className,
    query,
    pageIdentifier,
    shouldOpenDatePickerOnMount,
    onRemoveCoupon,
    setSortKey,
    filterFormProps,
    onSearchFilter,
  } = props;

  const placeRedirect = usePlaceRedirect();
  const language = useSelector(getLanguage);
  const areaName = useSelector(getAreaName);
  const dispatch = useDispatch();

  const { isSP } = useDeviceTypes();
  const { isCustomFixedBarHidden } = useCustomFixedStyling();
  const bannerHeight = useSelector(getHeightIfSmartBannerPresent);
  const ranData = useSelector(getRanData);

  const [isOpenDatePicker, setIsOpenDatePicker] = useState(false);

  const onSubmit = ({ place, shouldResetFilter, formData }: SubmitParams) => {
    const queryString = new URLSearchParams(formData as any).toString();
    let searchQueryString = queryString;
    const queryObj = Object.fromEntries(formData.entries());

    if (shouldResetFilter && place.category !== 'PROVIDER') {
      const queryObject = parseQueryString(queryString);
      searchQueryString = stringify(getAllowedRedirectSearchQuery(queryObject));
    }
    if (place.category !== 'PROVIDER') {
      dispatch(updateAreaName(place.name));
      savedSearchStore(language, place, queryObj);
    }
    placeRedirect(place, searchQueryString);
  };
  const isDatedSearch =
    query?.startDate || query?.endDate || autoFilledCheckInDate() || autoFilledCheckOutDate();
  const onPage = isDatedSearch ? 'providerListDated' : 'providerListUndated';

  const filterContainer = (
    <FilterContainer
      onPage={onPage}
      pathId={pageIdentifier}
      query={query}
      onSearch={onSearchFilter}
      onRemoveCoupon={onRemoveCoupon}
      setSortKey={setSortKey}
    />
  );

  const toggleButton = ({ onClick }: SearchPanelProps) => (
    <button
      type="button"
      className={cx(styles.formActiveButton)}
      onClick={onClick}
      aria-label="active search panel"
      data-testid="searchForm-toggleSearchPanel-button"
    />
  );

  return (
    <div
      className={cx(className, styles.wrapper, isCustomFixedBarHidden && styles.fixedBarHidden)}
      data-testid="providerList-searchForm-wrapper"
      style={isSP && !isCustomFixedBarHidden ? { marginTop: bannerHeight } : undefined}
    >
      <SearchFormWithRef
        onPage={onPage}
        ref={searchFormRef}
        searchFormClassName={styles.searchFormContainer}
        wrapperClassName={styles.searchFormWrapper}
        query={query}
        translateId="Top.Search_Button"
        currentPlaceName={areaName}
        defaultText={areaName}
        isAutoOpenDatePicker={isOpenDatePicker}
        setIsAutoOpenDatePicker={setIsOpenDatePicker}
        pageIdentifier={pageIdentifier}
        shouldOpenDatePickerOnMount={shouldOpenDatePickerOnMount}
        onSubmit={onSubmit}
        shouldSubmitOnInputChange={true}
        toggleButton={toggleButton}
        expandPanelClassName={styles.active}
        filterForm={isSP ? filterContainer : <FilterForm {...filterFormProps} />}
        hiddenInput={
          <>
            <SalesPromotionInput {...query} />
            <RanDataFormInputs ranData={ranData} />
          </>
        }
      ></SearchFormWithRef>
    </div>
  );
}

export default React.memo(SearchBar);
